<template>
    <div id="c-bot-chat-list">
        <a-table
            :columns="columns"
            :dataSource="getData()"
            :locale="{ emptyText: $t('message.noLiveSupportFound') }"
            :pagination="false"
            :rowClassName="rowClassName"
            class="m-0 table-custom custom-table"
            rowKey="_id"
            size="small"
            @change="tableChange"
        >
            <div :key="`customDate-(${item._id})(${$i18n.locale})`" slot="customDate" slot-scope="data, item">
                {{ item.createdDate | dateShorted }}
            </div>
            <div :key="`chatDurations-(${item._id})(${$i18n.locale})`" slot="chatDurations" slot-scope="data, item">
                <PassedTimeCounter v-if="item.status === 'chatbot'" :date="item.createdDate" suffix=" saniye" />
            </div>

            <div :key="`actions-(${item._id})(${$i18n.locale})`" slot="actions" slot-scope="data, item">
                <div class="d-flex justify-content-end" style="margin-right: -10px !important">
                    <a-popover placement="bottom" title="" trigger="click">
                        <template slot="content">
                            <div class="content">
                                <div
                                    v-if="
                                        (item.users || []).filter((w) => w === user._id).length === 0 &&
                                        !observing.includes(item._id)
                                    "
                                    class="d-flex flex-row"
                                >
                                    <div class="justify-content-start">
                                        <watch-icon />
                                    </div>
                                    <div
                                        class="justify-content-end pl-3 popoverFont cursorPointer"
                                        @click="observe(item)"
                                    >
                                        {{ $t("message.watch") }}
                                    </div>
                                </div>
                            </div>
                        </template>
                        <md-button class="custom-button" data-v-step="15">
                            <Actions></Actions>
                        </md-button>
                    </a-popover>

                    <a-popover title="" trigger="click">
                        <template slot="content">
                            <div class="px-2">
                                <b-row class="mb-3">
                                    <b-col md="6" style="color: #9ba2b6; font-weight: bold"
                                        >{{ $t("message.country") }}:
                                    </b-col>
                                    <b-col md="6">{{ item.geoLocation ? item.geoLocation.country : "N/A" }}</b-col>
                                </b-row>
                                <b-row v-if="$_.get(item, 'geoLocation.city', false)" class="mb-3">
                                    <b-col md="6" style="color: #9ba2b6; font-weight: bold"
                                        >{{ $t("message.city") }}:
                                    </b-col>
                                    <b-col md="6">{{ item.geoLocation.city }}</b-col>
                                </b-row>
                                <b-row class="mb-3">
                                    <b-col md="6" style="color: #9ba2b6; font-weight: bold"
                                        >{{ $t("message.browser") }}:
                                    </b-col>
                                    <b-col md="6"
                                        >{{
                                            item.visitor.browser
                                                ? item.visitor.browser
                                                : getUserAgentInfo(item.userAgent, "browser")
                                        }}
                                    </b-col>
                                </b-row>
                                <b-row class="mb-3">
                                    <b-col md="6" style="color: #9ba2b6; font-weight: bold"
                                        >{{ $t("message.os") }}:
                                    </b-col>
                                    <b-col md="6">
                                        {{ item.visitor.os ? item.visitor.os : getUserAgentInfo(item.userAgent, "os") }}
                                    </b-col>
                                </b-row>
                                <b-row class="mb-3">
                                    <b-col md="6" style="color: #9ba2b6; font-weight: bold"
                                        >{{ $t("message.visitStart") }}:
                                    </b-col>
                                    <b-col md="6">{{ item.createdDate | date }}</b-col>
                                </b-row>
                            </div>
                        </template>
                        <md-button class="custom-button" data-v-step="14">
                            <Information></Information>
                        </md-button>
                    </a-popover>

                    <md-button
                        v-if="
                            (item.users || []).filter((w) => w === user._id).length === 0 &&
                            !observing.includes(item._id)
                        "
                        v-tippy="{
                            followCursor: true,
                            placement: 'bottom-end',
                            theme: isDarkMode ? 'light-border' : 'google',
                        }"
                        :content="$t('message.watch')"
                        class="custom-button"
                        data-v-step="13"
                        @click="observe(item)"
                    >
                        <a-icon style="font-size: 14px; color: #49c550" theme="filled" type="eye" />
                    </md-button>
                </div>
            </div>
        </a-table>
        <div class="d-flex justify-content-end mt-3" style="margin-bottom: 100px">
            <Pagination
                :page-change="pageChange"
                :page-sizes="pageSizes"
                :total="totalChatCount"
                data-v-step="17"
                pageSizeLocalStorageKey="chatbot-chatlist-page-size"
            />
        </div>
    </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";
import { library } from "@fortawesome/fontawesome-svg-core";
import { faCog, faComment, faCommentAlt, faCommentDots, faEye } from "@fortawesome/free-solid-svg-icons";
import PassedTimeCounter from "./PassedTimeCounter";
import PassedTimeCounterTimeFormat from "./PassedTimeCounterTimeFormat";
import CommentDots from "./svg/CommentDots";
import Info from "./svg/Info";
import Cog from "./svg/Cog";
import * as _ from "lodash";
import { log2 } from "../utils/log2";
import { tableSorter } from "../utils/utils";
import Information from "./svg/Information";
import Actions from "./svg/Actions";
import WatchIcon from "./svg/WatchIcon";
import Pagination from "./Pagination";
import { sendSocketMessage, socketMessageStream } from "../utils/socketStream";
import { filter, map } from "rxjs/operators";
import moment from "moment";
import { getPersistenceData } from "../utils/persistenceStorage";

library.add(faCommentDots, faCog, faEye, faComment, faCommentAlt);

const log = log2("BotChatList");
export default {
    name: "BotChatList",
    components: {
        Actions,
        Information,
        PassedTimeCounter,
        PassedTimeCounterTimeFormat,
        CommentDots,
        Info,
        Cog,
        Pagination,
        WatchIcon,
    },
    props: {},
    computed: {
        ...mapState({
            user: (state) => state.auth.user,
            site: (state) => state.site.site,
            isRoot: (state) => state.auth.user.role.isRoot,
            observing: (state) => state.chat.observing,
            totalChatCount: (state) => state.chat.stats.chatbot,
            settings: (state) => state.setting.settings,
            socketConnected: (state) => state.socketStatus.socket_connected,
            rows: (state) => _.sortBy(state.chat.latestChatbotChats, (x) => moment(x.createdDate).toDate().getTime()),
        }),
        ...mapGetters({
            findConversationByVisitorId: "chat/findConversationByVisitorId",
            isDarkMode: "user/isDarkMode",
        }),
        columns() {
            let cols = [];

            cols = [
                ...cols,
                {
                    title: this.$t("message.fullName"),
                    dataIndex: "fullname",
                    name: "Fullname",
                    ...tableSorter("fullname", "string"),
                },
                {
                    title: this.$t("message.email"),
                    dataIndex: "email",
                    name: "Email",
                    ...tableSorter("email", "string"),
                },
                {
                    title: this.$t("message.date"),
                    dataIndex: "createdDate",
                    name: "createddate",
                    scopedSlots: { customRender: "customDate" },
                    width: "10%",
                    ...tableSorter("createdDate", "date"),
                },
                {
                    title: this.$t("message.chatDuration"),
                    key: "chatDurations",
                    name: "chatdurations",
                    scopedSlots: { customRender: "chatDurations" },
                    ...tableSorter("chatDurations", "date"),
                    width: "10%",
                },
                {
                    title: this.$t("message.transactions"),
                    key: "actions",
                    name: "actions",
                    width: "5%",
                    scopedSlots: { customRender: "actions" },
                    align: "right",
                },
            ];

            return cols;
        },
    },
    data() {
        const lsKey = "chatbot-chatlist-page-size";
        return {
            pageSizes: ["10", "25", "50", "100"],
            currentPage: 0,
            perPage: !isNaN(getPersistenceData(lsKey) || "undefined") ? parseInt(getPersistenceData(lsKey)) : 10,
            activeModalActions: null,
            activeModalInfo: null,
            currentSort: {
                columnKey: "createdDate",
                order: "ascend",
            },
            pageSize: !isNaN(getPersistenceData(lsKey) || "undefined") ? parseInt(getPersistenceData(lsKey)) : 10,
            loading: false,
            dataFetcherSubscriber: null,
            timerId: null,
            lastDataFetchTS: Date.now(),
        };
    },
    mounted() {
        log("mounted");
        this.dataFetcherSubscriber = socketMessageStream
            .pipe(
                filter((x) => x.cmd === "live_chatbot_conversations_response"),
                map((x) => x.data)
            )
            .subscribe((data) => {
                const { conversations } = data;
                this.loading = false;
                this.setLatestChatBotChats(conversations);
            });
        this.fetchPagedData();
        this.timerId = setInterval(this.fetchPagedData, 2000);
    },
    beforeDestroy() {
        if (this.dataFetcherSubscriber) this.dataFetcherSubscriber.unsubscribe();
        if (this.timerId) clearInterval(this.timerId);
    },
    updated() {},
    methods: {
        ...mapActions({
            joinConversation: "chat/joinConversation",
            getSettings: "setting/getSettings",
            setLatestChatBotChats: "chat/setLatestChatBotChats",
        }),
        fetchPagedData: _.debounce(function () {
            log("fetchPagedData called");
            const tookTime = Date.now() - this.lastDataFetchTS;
            const isNotValid = this.loading && !this.socketConnected && tookTime <= 5000;
            if (isNotValid) {
                log.warn("[ChatList] fetchPagedData: socket is not connected, skip fetching data", {
                    tookTime,
                    loading: this.loading,
                    socketConnected: this.socketConnected,
                });
                return;
            }
            this.loading = true;
            this.lastDataFetchTS = Date.now();
            sendSocketMessage("user get-live-chatbot-conversations", {
                page: this.currentPage,
                perPage: this.perPage,
                sortCol: this.currentSort.columnKey,
                sortDirection: this.currentSort.order === "ascend" ? "ASC" : "DESC",
                siteId: this.site._id,
            });
        }, 1000),

        tableChange(pagination, filters, sorter) {
            log("tableChange", { pagination, filters, sorter });
            const newSort = {
                columnKey: sorter.columnKey,
                order: sorter.order,
            };
            const oldSort = this.currentSort;
            if (!_.isEqual(newSort, oldSort)) {
                this.currentSort = newSort;
                this.$forceUpdate();
                this.fetchPagedData();
            }
        },
        getData() {
            return this.rows;
        },
        getUserAgentInfo(userAgent, info) {
            const parser = require("ua-parser-js");
            const ua = parser(userAgent || "-");
            if (info === "os") {
                return `${ua.os.name} ${ua.os.version}`;
            } else if (info === "browser") {
                return `${ua.browser.name} ${ua.browser.major}`;
            }
        },
        observe(conversation) {
            this.joinConversation({ conversation, observe: true });
        },

        pageChange(page, perPage) {
            this.currentPage = page - 1;
            this.perPage = perPage;
        },
        rowClassName(record, index) {
            return record.status === "lost" ? "red" : "blue";
        },
    },
    watch: {
        page(newValue, oldValue) {
            if (newValue) {
                this.fetchPagedData();
            }
        },
        perPage(newValue, oldValue) {
            if (newValue) {
                this.fetchPagedData();
            }
        },
        totalChatCount(newValue, oldValue) {
            if (newValue) {
                const total = newValue.length;
                const maxPage = Math.ceil(total / this.perPage);
                if (this.currentPage > maxPage) {
                    this.currentPage = 0;
                } else {
                    if (this.rows.length < this.perPage) {
                        this.fetchPagedData();
                    }
                }
                if (total < this.perPage) {
                    this.fetchPagedData();
                }
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.modal-actions {
    &.active {
        &:before,
        .content {
            display: block;
            z-index: 99999;
        }

        .trigger {
            z-index: 8;
        }
    }

    &:before,
    .content {
        display: none;
    }

    &:before {
        content: "";
        position: fixed;
        top: 0;
        right: 0;
        bottom: -100px;
        left: 0;
        background-color: rgba(0, 0, 0, 0.4);
        z-index: 2;
    }

    .trigger {
        position: relative;
    }

    .content {
        width: 350px;
        left: -10px;
        top: 22px;
        z-index: 3;

        .btn {
            text-align: left;
            border-radius: 0;
            height: 40px;
            font-size: 18px;
        }

        &:before {
            /*position: absolute;
            top: -32px;
            left: 0px;*/
            width: 50px;
            height: 50px;
            border-radius: 50%;
            content: "";
            background-color: #ffffff;
            z-index: 1;
        }
    }
}

.empty {
    position: relative !important;
    margin-top: 0px !important;
    margin-bottom: 5px;
}

.dark-mode .anticon {
    color: #000;
}
</style>

<style>
.ant-popover-content .ant-popover-inner {
    box-shadow: none;
}
</style>

<style lang="scss" scoped>
.new-theme {
    .ellipsis {
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
        max-width: 200px;
    }

    .custom-button {
        max-width: 18px;
        min-width: 18px;
        max-height: 18px;
        border-radius: 50%;
        overflow: hidden;
        display: flex;
        justify-content: center;
        align-items: center;
    }

    .custom-button svg {
        width: 14px;
        height: 14px;
    }

    .cursorPointer {
        cursor: pointer;
    }

    .cursorPointer:hover {
        font-weight: 600;
    }

    .popoverFont {
        font-family: Poppins;
        font-size: 14px;
        color: #181c32;
        font-weight: 300;
    }
}
</style>
